import React from 'react';

import AraByUserTable from '../../ara/components/AraByUserTable';
import BillDetailsTable from '../../ara/components/BillDetails';
import {
    AraUserTableRow,
    AraUserSortKey,
    BillDetailRow,
    BillSortKey,
    DueStatus,
} from '../../ara/api';

import {
    fetchKeyAccountArSummary,
    fetchKeyAccountArByPerson,
    fetchKeyAccountBills,
    downloadKeyAccountBills,
    KeyAccountArSummary,
    SnapshotRef,
} from '../api';
import SummaryStrip from '../components/SummaryStrip';
import { ConversionType } from '../components/UnitToggle';
import { formatCurrency, formatDays, formatSnapshotDate } from '../components/format';

interface Props {
    customerCode: string;
    customerName: string;
    conversionType: ConversionType;
    onConversionChange: (type: ConversionType) => void;
}

const ReceivablesTab: React.FC<Props> = ({
    customerCode,
    customerName,
    conversionType,
    onConversionChange,
}) => {
    const [snapshot, setSnapshot] = React.useState<SnapshotRef | null>(null);
    const [error, setError] = React.useState<string>('');

    // customer summary strip
    const [summary, setSummary] = React.useState<KeyAccountArSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = React.useState<boolean>(false);
    const [hasLoadedOnce, setHasLoadedOnce] = React.useState<boolean>(false);

    // collection-responsibility table (rows[0] = TOTAL)
    const [rows, setRows] = React.useState<AraUserTableRow[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [totalItems, setTotalItems] = React.useState<number>(0);
    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [sortBy, setSortBy] = React.useState<AraUserSortKey>('total_outstanding');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

    // bill drill-down
    const [selectedUser, setSelectedUser] = React.useState<string | null>(null);
    const [selectedUserName, setSelectedUserName] = React.useState<string | null>(null);
    const [bills, setBills] = React.useState<BillDetailRow[]>([]);
    const [billLoading, setBillLoading] = React.useState<boolean>(false);
    const [billTotalItems, setBillTotalItems] = React.useState<number>(0);
    const [billPage, setBillPage] = React.useState<number>(1);
    const [billPageSize, setBillPageSize] = React.useState<number>(20);
    const [billSortBy, setBillSortBy] = React.useState<BillSortKey>('due_date');
    const [billSortOrder, setBillSortOrder] = React.useState<'asc' | 'desc'>('asc');
    const [billSearch, setBillSearch] = React.useState<string>('');
    const [debouncedBillSearch, setDebouncedBillSearch] = React.useState<string>('');
    const [billDueStatus, setBillDueStatus] = React.useState<DueStatus | undefined>(undefined);
    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedBillSearch(billSearch.trim());
            setBillPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [billSearch]);

    // summary strip
    React.useEffect(() => {
        if (!customerCode) return;

        setSummaryLoading(true);
        setError('');

        fetchKeyAccountArSummary({ customer_code: customerCode })
            .then((resp) => {
                setSnapshot(resp.snapshot);
                setSummary(resp.summary);
            })
            .catch((err: unknown) => {
                const isForbidden = (err as any)?.status === 403;
                setError(
                    isForbidden
                        ? 'You are not allowed to access this resource.'
                        : err instanceof Error
                            ? err.message
                            : 'Failed to fetch receivables data'
                );
                setSummary(null);
            })
            .finally(() => {
                setSummaryLoading(false);
                setHasLoadedOnce(true);
            });
    }, [customerCode]);

    // collection-responsibility breakdown
    React.useEffect(() => {
        if (!customerCode) return;

        setLoading(true);

        fetchKeyAccountArByPerson({
            customer_code: customerCode,
            page,
            per_page: pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
        })
            .then((resp) => {
                setRows(resp.rows);
                setTotalItems(resp.total_items);
            })
            .catch(() => {
                setRows([]);
                setTotalItems(0);
            })
            .finally(() => setLoading(false));
    }, [customerCode, page, pageSize, sortBy, sortOrder]);

    // bill details
    React.useEffect(() => {
        if (!customerCode || !selectedUser) return;

        setBillLoading(true);

        fetchKeyAccountBills({
            customer_code: customerCode,
            ecode: selectedUser,
            search: debouncedBillSearch || undefined,
            due_status: billDueStatus || undefined,
            page: billPage,
            per_page: billPageSize,
            sort_by: billSortBy,
            sort_order: billSortOrder,
        })
            .then((resp) => {
                setBills(resp.items);
                setBillTotalItems(resp.total_items);
            })
            .catch(() => {
                setBills([]);
                setBillTotalItems(0);
            })
            .finally(() => setBillLoading(false));
    }, [customerCode, selectedUser, debouncedBillSearch, billDueStatus, billPage, billPageSize, billSortBy, billSortOrder]);

    const handleDownloadBillsExcel = async () => {
        if (!selectedUser) return;
        try {
            setDownloadingExcel(true);
            const blob = await downloadKeyAccountBills({
                customer_code: customerCode,
                ecode: selectedUser,
                search: debouncedBillSearch || undefined,
                due_status: billDueStatus || undefined,
                sort_by: billSortBy,
                sort_order: billSortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Bill_Details_${customerName}_${selectedUserName}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Bill details download failed', err);
        } finally {
            setDownloadingExcel(false);
        }
    };

    const unitLabel = conversionType === 'INR' ? '' : ` (${conversionType})`;

    if (error) {
        return (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
            </div>
        );
    }

    if (hasLoadedOnce && !snapshot) {
        return (
            <div className="text-center py-12 text-gray-500">
                No accounts receivable snapshot available yet.
            </div>
        );
    }

    if (selectedUser) {
        return (
            <div className="flex flex-col" style={{ minHeight: '60vh' }}>
                <BillDetailsTable
                    data={bills}
                    loading={billLoading}
                    customerName={customerName}
                    collectionResponsibilityName={selectedUserName}
                    page={billPage}
                    pageSize={billPageSize}
                    totalItems={billTotalItems}
                    sortBy={billSortBy}
                    sortOrder={billSortOrder}
                    onSortChange={(col) => {
                        setBillPage(1);
                        if (billSortBy === col) {
                            setBillSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
                        } else {
                            setBillSortBy(col);
                            setBillSortOrder('asc');
                        }
                    }}
                    onPageChange={setBillPage}
                    onPageSizeChange={(s) => {
                        setBillPage(1);
                        setBillPageSize(s);
                    }}
                    onBack={() => {
                        setSelectedUser(null);
                        setSelectedUserName(null);
                        setBillSearch('');
                        setBillDueStatus(undefined);
                    }}
                    conversionType={conversionType}
                    onConversionChange={onConversionChange}
                    search={billSearch}
                    dueStatus={billDueStatus}
                    onSearchChange={(v) => {
                        setBillSearch(v);
                        setBillPage(1);
                    }}
                    onDueStatusChange={(v) => {
                        setBillDueStatus(v);
                        setBillPage(1);
                    }}
                    onDownloadExcel={handleDownloadBillsExcel}
                    downloadingExcel={downloadingExcel}
                />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <SummaryStrip
                title="Customer Receivables Summary"
                asOf={formatSnapshotDate(snapshot)}
                loading={summaryLoading && !summary}
                items={
                    summary
                        ? [
                            { label: 'Credit Period (Days)', value: formatDays(summary.credit_period) },
                            { label: `Total Outstanding${unitLabel}`, value: formatCurrency(summary.total_outstanding, conversionType) },
                            { label: `Not Due${unitLabel}`, value: formatCurrency(summary.not_due, conversionType) },
                            { label: `Total Overdue${unitLabel}`, value: formatCurrency(summary.total_overdue, conversionType), valueClassName: summary.total_overdue > 0 ? 'text-red-700' : '' },
                            { label: `1–30${unitLabel}`, value: formatCurrency(summary.bucket_1_30, conversionType) },
                            { label: `31–60${unitLabel}`, value: formatCurrency(summary.bucket_31_60, conversionType) },
                            { label: `61–90${unitLabel}`, value: formatCurrency(summary.bucket_61_90, conversionType) },
                            { label: `91–120${unitLabel}`, value: formatCurrency(summary.bucket_91_120, conversionType) },
                            { label: `121–180${unitLabel}`, value: formatCurrency(summary.bucket_121_180, conversionType) },
                            { label: `>180${unitLabel}`, value: formatCurrency(summary.bucket_over_180, conversionType) },
                            { label: `Legal${unitLabel}`, value: formatCurrency(summary.legal, conversionType) },
                            { label: `UAR${unitLabel}`, value: formatCurrency(summary.unapplied_receipts, conversionType) },
                            { label: `Net Outstanding${unitLabel}`, value: formatCurrency(summary.net_outstanding, conversionType) },
                            { label: 'DSO (Days)', value: formatDays(summary.dso) },
                        ]
                        : []
                }
            />

            <div className="flex flex-col" style={{ minHeight: '50vh' }}>
                <AraByUserTable
                    data={rows}
                    loading={loading}
                    onUserClick={(ecode, name) => {
                        setSelectedUser(ecode);
                        setSelectedUserName(name);
                        setBillPage(1);
                        setBillSearch('');
                        setBillDueStatus(undefined);
                    }}
                    conversionType={conversionType}
                    onConversionChange={onConversionChange}
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageChange={setPage}
                    onPageSizeChange={(s) => {
                        setPage(1);
                        setPageSize(s);
                    }}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={(key) => {
                        setPage(1);
                        if (sortBy === key) {
                            setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
                        } else {
                            setSortBy(key);
                            setSortOrder('asc');
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ReceivablesTab;
